import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { STLExporter } from 'three/addons/exporters/STLExporter.js';
import { parseSTL, selectPourPoints } from './geometry.js';

const $ = (id) => document.getElementById(id);
const form = $('settings');
let source = null,
  worker = null,
  timeout = null,
  archiveURL = null,
  funnelURL = null,
  generated = null;
let currentView = 'model',
  loadedName = '',
  renderAvailable = true;
let manualHoles = [],
  placing = false,
  shapeMesh = null,
  shapeBounds = null,
  pointerStart = null;
let renderer, scene, camera, orbit, content, grid;
let draft = null,
  previewWorker = null,
  previewTimer = null,
  previewTimeout = null;
let buildingSettings = null;
let diagnosisURL = null;
let repairEnabled = false,
  adaptationEnabled = false,
  removeDetachedEnabled = false,
  pendingProposal = null;
let acceptedAdaptMethod = 'directional';
const host = $('canvas-host');
const previewMode = new URLSearchParams(location.search).get('preview');
const basicPreview = previewMode === 'basic';
function viewerStatus(code, detail = '') {
  $('viewer-state').textContent = `Preview status: ${code}${detail ? ` · ${detail}` : ''}`;
}

function initializeViewer() {
  if (previewMode === 'off') {
    renderAvailable = false;
    viewerStatus('DISABLED');
    $('viewer-error').textContent =
      '3D preview is switched off for troubleshooting. You can still generate and download your mold kit.';
    $('viewer-error').hidden = false;
    return;
  }
  try {
    const failures = [];
    let reducedGraphics = false;
    const compact = matchMedia('(max-width: 700px)').matches;
    for (const antialias of compact || basicPreview ? [false] : [true, false]) {
      const canvas = document.createElement('canvas');
      let status = '';
      canvas.addEventListener('webglcontextcreationerror', (event) => {
        status = event.statusMessage || '';
      });
      try {
        renderer = new THREE.WebGLRenderer({
          canvas,
          antialias,
          alpha: false,
          ...(basicPreview ? { precision: 'mediump' } : {}),
        });
        reducedGraphics = !antialias;
        break;
      } catch (error) {
        failures.push(status || error.message);
      }
    }
    if (!renderer) throw Error(failures.join(' / '));
    renderer.setPixelRatio(reducedGraphics || compact ? 1 : Math.min(devicePixelRatio, 2));
    renderer.setClearColor(0xeeece4, 1);
    renderer.shadowMap.enabled = !reducedGraphics;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = basicPreview ? THREE.NoToneMapping : THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.5;
    host.append(renderer.domElement);
    scene = new THREE.Scene();
    scene.up.set(0, 0, 1);
    camera = new THREE.PerspectiveCamera(33, 1, 0.1, 5000);
    camera.up.set(0, 0, 1);
    orbit = new OrbitControls(camera, renderer.domElement);
    orbit.enableDamping = true;
    orbit.minDistance = 10;
    orbit.maxDistance = 1400;
    scene.add(new THREE.HemisphereLight(0xfffbef, 0x9b9484, 3));
    const key = new THREE.DirectionalLight(0xffecd7, 3.5);
    key.position.set(-100, -80, 220);
    key.castShadow = true;
    key.shadow.mapSize.set(compact ? 1024 : 2048, compact ? 1024 : 2048);
    key.shadow.camera.left = -300;
    key.shadow.camera.right = 300;
    key.shadow.camera.top = 300;
    key.shadow.camera.bottom = -300;
    key.shadow.bias = -0.0004;
    scene.add(key);
    const fill = new THREE.DirectionalLight(0xe4e8ff, 2);
    fill.position.set(100, 100, 100);
    scene.add(fill);
    content = new THREE.Group();
    scene.add(content);
    grid = new THREE.GridHelper(600, 40, 0xc8c1b3, 0xd9d3c6);
    grid.rotation.x = Math.PI / 2;
    grid.material.transparent = true;
    grid.material.opacity = 0.28;
    scene.add(grid);
    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(3000, 3000),
      new THREE.ShadowMaterial({ opacity: 0.12 })
    );
    floor.receiveShadow = true;
    floor.position.z = -0.3;
    if (!reducedGraphics) scene.add(floor);
    let frameReported = false,
      shaderFailed = false;
    renderer.debug.onShaderError = (gl, program) => {
      shaderFailed = true;
      viewerStatus(
        'SHADER_FAILED',
        gl.getProgramInfoLog(program) || 'Graphics shader could not compile'
      );
    };
    renderer.domElement.addEventListener('webglcontextlost', (event) => {
      event.preventDefault();
      frameReported = false;
      viewerStatus('CONTEXT_LOST', 'Chrome interrupted the graphics connection');
    });
    renderer.domElement.addEventListener('webglcontextrestored', () => {
      shaderFailed = false;
      viewerStatus('RESTORING');
    });
    new ResizeObserver(() => {
      const w = host.clientWidth,
        h = host.clientHeight;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    }).observe(host);
    renderer.setAnimationLoop(() => {
      try {
        orbit.update();
        renderer.render(scene, camera);
        if (!frameReported && !shaderFailed && !renderer.getContext().isContextLost()) {
          viewerStatus('READY', basicPreview ? 'simple graphics' : 'WebGL 2');
          frameReported = true;
        }
      } catch (error) {
        renderer.setAnimationLoop(null);
        viewerStatus('RENDER_FAILED', error.message);
        console.error('3D preview render failed:', error);
      }
    });
  } catch (error) {
    viewerStatus('SETUP_FAILED', error.message);
    console.error('3D preview initialization failed:', error);
    renderer?.setAnimationLoop(null);
    renderer?.dispose();
    renderer?.domElement.remove();
    renderAvailable = false;
    $('viewer-error').textContent =
      `3D preview could not start. You can still generate and download your mold kit. ` +
      `Try reopening this page in Chrome or restarting Chrome. Details: ${error.message}`;
    $('viewer-error').hidden = false;
  }
}

function clearViewer() {
  if (!renderAvailable) return;
  while (content.children.length) {
    const child = content.children[0];
    content.remove(child);
    child.geometry?.dispose();
    child.material.dispose();
  }
}

function addMesh(positions, color, opacity = 1) {
  if (!renderAvailable) return;
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.computeVertexNormals();
  const mesh = new THREE.Mesh(
    geometry,
    new (basicPreview ? THREE.MeshLambertMaterial : THREE.MeshStandardMaterial)({
      color,
      ...(!basicPreview ? { roughness: 0.65, metalness: 0 } : {}),
      transparent: opacity < 1,
      opacity,
      side: THREE.DoubleSide,
      depthWrite: opacity === 1,
    })
  );
  mesh.castShadow = opacity === 1;
  mesh.receiveShadow = true;
  content.add(mesh);
  return mesh;
}

function resetCamera() {
  if (!renderAvailable || !content.children.length) return;
  const box = new THREE.Box3();
  content.children
    .filter((child) => !child.userData.fitIgnore)
    .forEach((child) => box.expandByObject(child));
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  const scale = Math.max(size.x, size.y, size.z, 50);
  orbit.target.copy(center);
  const direction = new THREE.Vector3(1.9, -2.8, 1.6).normalize();
  const right = new THREE.Vector3().crossVectors(direction, new THREE.Vector3(0, 0, 1)).normalize();
  const up = new THREE.Vector3().crossVectors(right, direction).normalize();
  const tangent = Math.tan(THREE.MathUtils.degToRad(camera.fov / 2));
  let distance = 30;
  for (const x of [box.min.x, box.max.x])
    for (const y of [box.min.y, box.max.y])
      for (const z of [box.min.z, box.max.z]) {
        const corner = new THREE.Vector3(x, y, z).sub(center);
        distance = Math.max(
          distance,
          corner.dot(direction) +
            Math.max(
              Math.abs(corner.dot(up)) / tangent,
              Math.abs(corner.dot(right)) / (tangent * camera.aspect)
            )
        );
      }
  camera.position.copy(center).addScaledVector(direction, distance * 1.25);
  camera.near = Math.max(0.1, scale / 1000);
  camera.far = scale * 60;
  camera.updateProjectionMatrix();
  orbit.update();
}

function settings() {
  if (worker && buildingSettings) return buildingSettings;
  const values = Object.fromEntries(
    [...new FormData(form)]
      .filter(
        ([key]) => key !== 'file' && key !== 'band' && key !== 'funnel' && key !== 'removeDetached'
      )
      .map(([key, value]) => [key, key === 'split' ? value : Number(value)])
  );
  values.funnel = form.elements.funnel.checked;
  values.band = form.elements.band.checked;
  values.removeDetached = removeDetachedEnabled || Boolean(form.elements.removeDetached?.checked);
  values.offset = Number(form.elements.offset.value);
  values.planeTilt = Number(form.elements.planeTilt.value);
  values.holes = manualHoles.slice(0, values.pourCount);
  values.fillDetached = repairEnabled;
  values.adaptForSplit = adaptationEnabled;
  values.adaptMethod = acceptedAdaptMethod;
  return values;
}

function modelPreview(fit = false) {
  if (!source) return;
  const geometry = parseSTL(source);
  const s = settings();
  const r = Math.PI / 180;
  geometry.applyMatrix4(
    new THREE.Matrix4().makeRotationFromEuler(
      new THREE.Euler(s.rotateX * r, s.rotateY * r, s.rotateZ * r, 'XYZ')
    )
  );
  geometry.computeBoundingBox();
  const height = geometry.boundingBox.max.z - geometry.boundingBox.min.z;
  if (height <= 0) return;
  geometry.scale(s.height / height, s.height / height, s.height / height);
  geometry.center();
  geometry.computeBoundingBox();
  const dimensions = geometry.boundingBox.getSize(new THREE.Vector3());
  geometry.translate(0, 0, dimensions.z / 2);
  geometry.computeBoundingBox();
  shapeBounds = {
    min: geometry.boundingBox.min.toArray(),
    max: geometry.boundingBox.max.toArray(),
  };
  clearViewer();
  shapeMesh = addMesh(geometry.attributes.position.array, 0xba693e);
  updateHolePreview(geometry.attributes.position.array, s);
  drawFilledRegions(generated || draft, dimensions.z / 2);
  drawPlanes(dimensions, geometry.attributes.position.array, s);
  geometry.dispose();
  $('model-label').textContent = loadedName;
  $('dimension-label').textContent = `${dimensions
    .toArray()
    .map((n) => n.toFixed(1))
    .join(' × ')} MM`;
  if (renderAvailable) grid.position.z = 0;
  if (fit) resetCamera();
}

function drawPlanes(dimensions, positions, s, originZ = dimensions.z / 2) {
  if (!renderAvailable || !$('show-planes').checked || !s.split) return;
  const active = (generated || draft)?.report.splitPlane;
  const angle = s.split === 'y' ? 90 : s.split === 'custom' ? s.planeAngle : active?.angle || 0;
  const tilt = s.split === 'z' ? 90 : s.split === 'custom' ? s.planeTilt : 0;
  const yaw = (angle * Math.PI) / 180,
    pitch = (tilt * Math.PI) / 180;
  const normal = new THREE.Vector3(
    Math.cos(yaw) * Math.cos(pitch),
    Math.sin(yaw) * Math.cos(pitch),
    Math.sin(pitch)
  );
  let min = Infinity,
    max = -Infinity;
  for (let i = 0; i < positions.length; i += 3) {
    const dot =
      normal.x * positions[i] +
      normal.y * positions[i + 1] +
      normal.z * (positions[i + 2] - dimensions.z / 2);
    min = Math.min(min, dot);
    max = Math.max(max, dot);
  }
  const size = Math.max(dimensions.x, dimensions.y, dimensions.z) + 2 * s.wall + 12;
  const makePlane = (width, height, n, center) => {
    const plane = new THREE.Mesh(
      new THREE.PlaneGeometry(width, height),
      new THREE.MeshBasicMaterial({
        color: 0x5b9ea0,
        transparent: true,
        opacity: 0.17,
        side: THREE.DoubleSide,
        depthWrite: false,
      })
    );
    plane.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), n);
    plane.position.copy(center);
    plane.userData.fitIgnore = true;
    content.add(plane);
    return plane;
  };
  if (s.pieces === 2)
    makePlane(
      size,
      size,
      normal,
      normal
        .clone()
        .multiplyScalar(((max - min) * s.offset) / 100)
        .add(new THREE.Vector3(0, 0, originZ))
    );
  else
    for (let i = 0; i < s.pieces; i++) {
      const a = yaw + (i * 2 * Math.PI) / s.pieces;
      const radius = Math.hypot(dimensions.x / 2, dimensions.y / 2) + s.wall;
      const plane = makePlane(
        radius,
        size,
        new THREE.Vector3(-Math.sin(a), Math.cos(a), 0),
        new THREE.Vector3((Math.cos(a) * radius) / 2, (Math.sin(a) * radius) / 2, originZ)
      );
      plane.quaternion.setFromRotationMatrix(
        new THREE.Matrix4().makeBasis(
          new THREE.Vector3(Math.cos(a), Math.sin(a), 0),
          new THREE.Vector3(0, 0, 1),
          new THREE.Vector3(Math.sin(a), -Math.cos(a), 0)
        )
      );
    }
}

function draftMessage(text, state = '') {
  $('draft-message').textContent = text;
  const el = $('draft-message').parentElement;
  el.classList.toggle('updating', state === 'updating');
  el.classList.toggle('failed', state === 'failed');
}

function stopPreview() {
  clearTimeout(previewTimer);
  clearTimeout(previewTimeout);
  previewWorker?.terminate();
  previewWorker = null;
}

function schedulePreview({ adapt = false } = {}) {
  stopPreview();
  if (!source || worker) return;
  if (placing) {
    draftMessage('Finish placing inlets to rebuild this split.');
    return;
  }
  draftMessage(
    adapt ? 'Testing local changes for your selected split…' : 'Checking your selected split…',
    'updating'
  );
  previewTimer = setTimeout(() => {
    if (!form.checkValidity()) {
      draftMessage(
        form.elements.split.value
          ? 'Complete the settings to preview this mold.'
          : 'Choose a split plane to check your mold.'
      );
      return;
    }
    const options = settings();
    const task = new Worker(new URL('./worker.js', import.meta.url), { type: 'module' });
    previewWorker = task;
    task.onmessage = ({ data }) => {
      if (previewWorker !== task) return;
      if (data.type === 'progress')
        draftMessage(
          `${adapt ? 'Adjustment' : 'Preview'} · ${data.message.toLowerCase()}…`,
          'updating'
        );
      if (data.type === 'error') {
        stopPreview();
        draftMessage(`Preview unavailable: ${data.message}`, 'failed');
        message('Could not prepare this split', data.message, true);
        showAdjustment(
          { report: { settings: options, adaptationAttempts: data.attempts } },
          data.message
        );
      }
      if (data.type === 'preview') {
        stopPreview();
        draft = data;
        document.querySelectorAll('[data-view]').forEach((b) => (b.disabled = false));
        showWallSummary(data.report);
        draftMessage('Preview is up to date · same mold geometry as the export.');
        if (placing) modelPreview();
        else setView('mold', true);
      }
      if (data.type === 'diagnostic') {
        stopPreview();
        showDiagnosis(data);
      }
      if (data.type === 'needs-adjustment') {
        stopPreview();
        showNeedsAdjustment(data);
      }
      if (data.type === 'proposal') {
        stopPreview();
        showProposal(data);
      }
      if (data.type === 'complete') {
        stopPreview();
        showResults(data);
      }
    };
    task.onerror = () => {
      if (previewWorker === task) {
        stopPreview();
        draftMessage(
          'Preview could not finish. Simplify the model or set cavity clearance to 0.',
          'failed'
        );
      }
    };
    previewTimeout = setTimeout(() => {
      if (previewWorker === task) {
        stopPreview();
        draftMessage(
          'Preview reached the 3-minute limit. Simplify the model or reduce clearance.',
          'failed'
        );
      }
    }, 180000);
    const copy = source.slice(0);
    task.postMessage({ buffer: copy, settings: options, selected: true, adapt }, [copy]);
  }, 900);
}

function showWallSummary(report) {
  const wall = report.wallThickness;
  $('wall-summary').hidden = false;
  $('wall-summary').classList.toggle('thin', wall.thin);
  $('wall-value').textContent =
    wall.minimumMm === null ? 'Not measurable' : `${wall.minimumMm.toFixed(2)} mm`;
  $('wall-state').textContent =
    wall.minimumMm === null
      ? 'No eligible samples'
      : wall.thin
        ? 'Below warning threshold'
        : 'Minimum sampled cavity wall';
  $('wall-method').textContent =
    `${wall.samples.toLocaleString()} valid readings · threshold ${wall.thresholdMm} mm · ${wall.parts.map((p) => `part ${p.part}: ${p.minimumMm === null ? 'n/a' : p.minimumMm.toFixed(2) + ' mm'}`).join(' / ')}`;
}

function wallMarkers(report, distance, lift) {
  if (!renderAvailable) return;
  report.wallThickness.parts.forEach((reading) => {
    if (!reading.location) return;
    const direction = new THREE.Vector3()
      .fromArray(report.pullDirections[reading.part - 1])
      .multiplyScalar(distance);
    const a = new THREE.Vector3().fromArray(reading.location.start).add(direction);
    a.z += lift;
    const b = new THREE.Vector3().fromArray(reading.location.end).add(direction);
    b.z += lift;
    const color = reading.minimumMm < report.wallThickness.thresholdMm ? 0xdc492a : 0xd68d29;
    const line = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([a, b]),
      new THREE.LineBasicMaterial({ color, depthTest: false, transparent: true })
    );
    line.renderOrder = 10;
    content.add(line);
    for (const p of [a, b]) {
      const dot = new THREE.Mesh(
        new THREE.SphereGeometry(2.2, 12, 8),
        new THREE.MeshBasicMaterial({ color, depthTest: false, transparent: true })
      );
      dot.position.copy(p);
      dot.renderOrder = 10;
      content.add(dot);
    }
  });
}

function updateHolePreview(positions, s) {
  $('hole-list').replaceChildren();
  try {
    const points = selectPourPoints(positions, shapeBounds, s.pourCount, s.pour, s.holes);
    points.forEach((point, i) => {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'hole-chip';
      chip.textContent = `${i + 1} · ${i < manualHoles.length ? 'placed ×' : 'auto'}`;
      chip.title =
        i < manualHoles.length
          ? 'Remove this manual inlet'
          : 'Automatically selected surface point';
      chip.disabled = i >= manualHoles.length;
      chip.onclick = () => {
        manualHoles.splice(i, 1);
        invalidate();
      };
      $('hole-list').append(chip);
      if (!renderAvailable) return;
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(s.pour / 2, 0.45, 8, 40),
        new THREE.MeshBasicMaterial({
          color: i < manualHoles.length ? 0xd64c29 : 0xf0c27a,
          depthTest: false,
        })
      );
      ring.position.set(point[0], point[1], point[2] + 0.5);
      ring.renderOrder = 5;
      content.add(ring);
      const stem = new THREE.Mesh(
        new THREE.CylinderGeometry(0.45, 0.45, 8, 8),
        new THREE.MeshBasicMaterial({ color: 0xa75b32, depthTest: false })
      );
      stem.rotation.x = Math.PI / 2;
      stem.position.set(point[0], point[1], point[2] + 4);
      stem.renderOrder = 5;
      content.add(stem);
    });
    $('hole-note').classList.remove('hole-feedback');
    $('hole-note').textContent = placing
      ? `Click to place ${Math.min(manualHoles.length + 1, s.pourCount)} of ${s.pourCount}. After all are placed, the next click replaces inlet 1.`
      : `${manualHoles.length} manually placed · ${s.pourCount - manualHoles.length} automatic. Click “Place inlets in 3D” to customize.`;
  } catch (error) {
    $('hole-note').textContent = error.message;
    $('hole-note').classList.add('hole-feedback');
  }
}

function placement(value) {
  const wasPlacing = placing;
  placing = value;
  $('place-holes').classList.toggle('active', value);
  $('place-holes').textContent = value ? '✓ Finish placing' : '⌖ Place inlets in 3D';
  $('placement-hint').hidden = !value;
  host.classList.toggle('placing', value);
  if (value) {
    stopPreview();
    setView('model');
    host.scrollIntoView({ block: 'center', behavior: 'instant' });
  } else if (wasPlacing && source && !worker && !generated) schedulePreview();
}

function setView(view, fit = false) {
  const visible = generated || draft;
  const failed = visible?.type === 'diagnostic';
  if (failed && view === 'walls') view = 'mold';
  currentView = view;
  document
    .querySelectorAll('[data-view]')
    .forEach((b) => b.classList.toggle('active', b.dataset.view === view));
  $('explode-control').hidden = view === 'model';
  if (!visible || view === 'model') {
    modelPreview(fit);
    return;
  }
  placement(false);
  clearViewer();
  const distance = (Number($('explode').value) / 100) * visible.report.moldDimensions[0] * 0.8;
  const minZ =
    visible.report.displayLift +
    Math.max(0, ...visible.report.pullDirections.map((n) => -n[2] * distance));
  visible.assembled.forEach((positions, i) => {
    const colors = [0x9eac98, 0xd8b184, 0xb7a5bf, 0xc9b276];
    const mesh = addMesh(
      positions,
      colors[i],
      failed ? 0.16 : view === 'xray' || view === 'walls' ? 0.28 : 1
    );
    if (mesh) {
      mesh.position.fromArray(visible.report.pullDirections[i]).multiplyScalar(distance);
      mesh.position.z += minZ;
    }
  });
  for (const accessory of visible.accessories || []) {
    const mesh = addMesh(accessory.positions, 0x657789, view === 'xray' ? 0.5 : 1);
    if (mesh) {
      mesh.position.fromArray(accessory.slideDirection).multiplyScalar(distance);
      mesh.position.z += minZ;
    }
  }
  if (view === 'xray') {
    const mesh = addMesh(visible.model, 0xc5673f, failed ? 0.12 : 1);
    if (mesh) mesh.position.z = minZ;
  }
  if (view === 'xray' || view === 'mold') drawFilledRegions(visible, minZ);
  if (failed) {
    visible.fragments.forEach((fragment) => {
      const mesh = addMesh(fragment.positions, 0xe5492e, 0.94);
      if (!mesh) return;
      mesh.material.depthTest = false;
      mesh.renderOrder = 50;
      mesh.position
        .fromArray(visible.report.pullDirections[fragment.part - 1])
        .multiplyScalar(distance);
      mesh.position.z += minZ;
    });
  }
  if (view === 'walls') wallMarkers(visible.report, distance, minZ);
  if (view === 'mold')
    drawPlanes(
      new THREE.Vector3().fromArray(visible.report.modelDimensions),
      visible.model,
      visible.report.settings,
      minZ
    );
  $('model-label').textContent =
    `${visible.report.pieces}-part mold · ${view === 'walls' ? 'sampled minimum wall locations' : visible.report.pieces === 2 ? visible.report.splitAxis.toUpperCase() + ' split' : 'radial sections'}`;
  $('dimension-label').textContent =
    `${visible.report.moldDimensions.map((n) => n.toFixed(1)).join(' × ')} MM · ${failed ? 'DETACHED REGIONS IN RED' : generated ? 'GENERATED' : 'PREVIEW'}`;
  if (fit) resetCamera();
}

function message(title, body, error = false) {
  $('status-title').textContent = title;
  $('status-message').textContent = body;
  $('process-status').classList.toggle('error', error);
}

function invalidate({ keepRepair = false, keepRemoveDetached = false } = {}) {
  if (!keepRepair) {
    repairEnabled = false;
    adaptationEnabled = false;
    acceptedAdaptMethod = 'directional';
  }
  if (!keepRemoveDetached) {
    removeDetachedEnabled = Boolean(form.elements.removeDetached?.checked);
  }
  pendingProposal = null;
  $('adjustment').hidden = true;
  $('adapt-model').hidden = true;
  $('adapt-model').disabled = true;
  $('fill-review').hidden = true;
  $('quick-download').hidden = true;
  $('quick-download').removeAttribute('href');
  $('quick-hint').textContent = form.elements.split.value
    ? 'Checking only your selected split. Model changes require your approval.'
    : 'Choose the number of pieces and a split plane to begin.';
  stopPreview();
  draft = null;
  $('wall-summary').hidden = true;
  $('diagnosis').hidden = true;
  if (diagnosisURL) {
    URL.revokeObjectURL(diagnosisURL);
    diagnosisURL = null;
    $('download-diagnosis').removeAttribute('href');
  }
  generated = null;
  if (funnelURL) URL.revokeObjectURL(funnelURL);
  funnelURL = null;
  $('funnel-download').hidden = true;
  $('download-funnel').removeAttribute('href');
  $('results').hidden = true;
  if (archiveURL) {
    URL.revokeObjectURL(archiveURL);
    archiveURL = null;
    $('download').removeAttribute('href');
  }
  document
    .querySelectorAll('[data-view]')
    .forEach((b) => (b.disabled = b.dataset.view !== 'model'));
  $('generate').disabled = !source;
  setView('model');
  schedulePreview();
}

function load(buffer, name, size) {
  try {
    const geo = parseSTL(buffer);
    const triangles = geo.attributes.position.count / 3;
    geo.dispose();
    source = buffer;
    loadedName = name;
    manualHoles = [];

    placement(false);
    $('filename').textContent = name;
    $('fileinfo').textContent =
      `${triangles.toLocaleString()} triangles · ${(size / 1024).toFixed(0)} KB`;
    if (!form.elements.split.value) form.elements.split.value = 'x';
    updatePlaneControls();
    invalidate();
    $('place-holes').disabled = !renderAvailable;
    resetCamera();
    message(
      form.elements.split.value ? 'Checking your selected split' : 'Choose how to split your mold',
      'The part count and plane stay as selected. We will propose model changes if this split needs them.'
    );
  } catch (error) {
    message('Could not open this model', error.message, true);
  }
}

async function loadFile(file) {
  if (!file || worker) return;
  if (!file.name.toLowerCase().endsWith('.stl') || file.size > 20 * 1024 * 1024) {
    message('Choose a smaller STL', 'Use an .stl file up to 20 MB.', true);
    return;
  }
  try {
    load(await file.arrayBuffer(), file.name, file.size);
  } catch {
    message('Could not read the file', 'Please choose the file again.', true);
  }
}

function example() {
  const points = [new THREE.Vector2(0, -40), new THREE.Vector2(21, -40)];
  for (let i = 0; i <= 48; i++) {
    const h = -40 + (i * 80) / 48;
    const radius = 23 + 3 * Math.cos((i / 48) * Math.PI * 8) + 2 * Math.sin((i / 48) * Math.PI);
    points.push(new THREE.Vector2(radius, h));
  }
  points.push(new THREE.Vector2(0, 40));
  const geometry = new THREE.LatheGeometry(points, 48);
  geometry.rotateX(Math.PI / 2);
  const mesh = new THREE.Mesh(geometry);
  const binary = new STLExporter().parse(mesh, { binary: true });
  load(binary.buffer, 'Ripple candle · example.stl', binary.byteLength);
  geometry.dispose();
}

function busy(value) {
  if (value) {
    placement(false);
    stopPreview();
    draftMessage('Generating the downloadable kit…', 'updating');
  }
  form.querySelectorAll('input,select,button').forEach((el) => (el.disabled = value));
  $('cancel').disabled = false;
  $('cancel').hidden = !value;
  $('progress').hidden = !value;
  $('process-status').classList.toggle('busy', value);
  if (!value) {
    $('generate').disabled = !source;
    $('place-holes').disabled = !source || !renderAvailable;
    updatePlaneControls();
  }
}

function endWorker() {
  clearTimeout(timeout);
  worker?.terminate();
  worker = null;
  buildingSettings = null;
  busy(false);
}

function generationFailed(title, detail) {
  endWorker();
  draftMessage(`${title}. ${detail}`, 'failed');
  message(title, detail, true);
}

function showResults(result) {
  repairEnabled = result.report.settings.fillDetached;
  adaptationEnabled = result.report.settings.adaptForSplit;
  acceptedAdaptMethod = result.report.settings.adaptMethod;
  if (result.report.settings.removeDetached !== undefined) {
    removeDetachedEnabled = result.report.settings.removeDetached;
    if (form.elements.removeDetached) form.elements.removeDetached.checked = removeDetachedEnabled;
  }
  for (const name of ['wall', 'band', 'funnel', 'pour', 'vent', 'wick', 'removeDetached']) {
    const field = form.elements[name];
    if (field && field.type === 'checkbox') field.checked = Boolean(result.report.settings[name]);
    else if (field) field.value = result.report.settings[name];
  }
  $('adjustment').hidden = true;
  $('adapt-model').hidden = true;
  $('adapt-model').disabled = true;
  pendingProposal = null;
  generated = result;
  archiveURL = URL.createObjectURL(new Blob([result.archive], { type: 'application/zip' }));
  $('download').href = archiveURL;
  if (funnelURL) URL.revokeObjectURL(funnelURL);
  funnelURL = result.funnelSTL
    ? URL.createObjectURL(new Blob([result.funnelSTL], { type: 'model/stl' }))
    : null;
  $('funnel-download').hidden = !funnelURL;
  if (funnelURL) $('download-funnel').href = funnelURL;
  else $('download-funnel').removeAttribute('href');
  $('quick-download').href = archiveURL;
  $('quick-download').hidden = false;
  $('quick-download').textContent = 'Download selected mold kit ↓';
  const r = result.report;

  showFillReview(result);
  showWallSummary(r);
  draftMessage('Preview is up to date · checked geometry and print files are ready.');
  $('quick-hint').textContent =
    `${r.settings.height} mm candle · ${r.pieces} mold sections · ${r.settings.pourCount} inlet(s). ${r.repair.applied ? 'Candle recesses filled: review amber changes.' : 'Change size or details in Advanced.'}`;
  const stats = [
    ['Candle volume', `${r.volumeMl} ml`],
    ['Mold sections', `${r.pieces} · ${r.enclosure}`],
    ['Closure', r.pieces === 2 ? 'Bands or straps' : 'Keys + collars + bands'],
    ...(r.funnel ? [['Wax reservoir', `${r.funnel.reservoirMl} ml · inlet 1`]] : []),
  ];
  $('stats').replaceChildren(
    ...stats.map(([title, value]) => {
      const el = document.createElement('div'),
        label = document.createElement('span'),
        text = document.createElement('strong');
      label.textContent = title;
      text.textContent = value;
      el.append(label, text);
      return el;
    })
  );
  $('warnings').replaceChildren(
    ...r.warnings.map((w) => {
      const li = document.createElement('li');
      li.textContent = w;
      return li;
    })
  );
  $('risk-title').textContent =
    r.releaseRisk === 'review'
      ? 'Release needs review · inspect before printing'
      : 'Before you print · sampled release check passed';
  $('risk-title').parentElement.open = Boolean(
    r.repair.applied || r.wallThickness.thin || !r.pullCheck.passed
  );
  $('results').hidden = false;
  document.querySelectorAll('[data-view]').forEach((b) => (b.disabled = false));
  if (!placing) setView(r.repair.applied ? 'xray' : 'mold', true);
  message(
    r.releaseRisk === 'review' ? 'Mold generated — review warnings' : 'Your mold kit is ready',
    `${r.pieces} validated solids, ${r.settings.pourCount} inlet(s), ${r.ventPoints?.length || 2} vent(s), and assembly notes. Cavity clearance: ${r.clearanceRange.join('–')} mm. Generated on your device.`
  );
}

function showDiagnosis(data) {
  if (data.report.scan) {
    for (const [name, value] of Object.entries(data.report.settings)) {
      const field = form.elements.namedItem(name);
      if (field && !Array.isArray(value)) {
        if (field.type === 'checkbox') field.checked = value;
        else field.value = value;
      }
    }
    updatePlaneControls();
  }
  draft = data;
  generated = null;
  if (funnelURL) URL.revokeObjectURL(funnelURL);
  funnelURL = null;
  $('funnel-download').hidden = true;
  $('download-funnel').removeAttribute('href');
  $('results').hidden = true;
  $('wall-summary').hidden = true;
  $('diagnosis').hidden = false;
  if (archiveURL) {
    URL.revokeObjectURL(archiveURL);
    archiveURL = null;
  }
  $('download').removeAttribute('href');
  const diagnosis = data.report.diagnosis;
  $('fill-offer').hidden = false;
  $('fill-detached').disabled = false;
  $('fill-detached').textContent = diagnosis.fillableRegionCount
    ? 'Fill recesses & rebuild'
    : 'Try filling & validate';
  const totalVolume = data.fragments.reduce((sum, fragment) => sum + fragment.volumeMm3, 0);
  const reasons = [
    ...new Set(data.fragments.map((fragment) => fragment.fillUnavailableReason).filter(Boolean)),
  ];
  $('fill-offer-description').textContent = data.report.fillAttempt?.reasons?.length
    ? `Fill attempted: ${data.report.fillAttempt.reasons.join(' ')}`
    : `${data.fragments.length} loose material region(s), ${totalVolume.toFixed(4)} mm³ in total. Filling adds their volume to the candle and removes the loose mold cores. ${diagnosis.fillableRegionCount ? `${diagnosis.fillableRegionCount} region(s) passed the initial interior-core check. ` : 'No region passed the initial interior-core check; you can still try a fill and validate the result. '}${reasons.join(' ')}`;
  showFillReview(data);
  const parts = diagnosis.affectedParts
    .map(
      (p) =>
        `part ${p.part} (${p.componentCount - 1} detached ${p.componentCount === 2 ? 'region' : 'regions'})`
    )
    .join(', ');
  $('diagnosis-title').textContent = `Disconnected material in ${parts}`;
  $('diagnosis-explanation').textContent = diagnosis.explanation;
  $('diagnosis-suggestions').replaceChildren(
    ...[
      'Propose model changes to fill the highlighted recesses or directional undercuts while keeping this split.',
      ...diagnosis.suggestions.filter(
        (text) => !/different plane|more sections|different split|different orientation/i.test(text)
      ),
    ].map((text) => {
      const li = document.createElement('li');
      li.textContent = text;
      return li;
    })
  );
  $('fragment-list').replaceChildren(
    ...data.fragments.map((fragment, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = `Focus part ${fragment.part} · region ${fragment.component - 1} · ${fragment.dimensionsMm.map((n) => n.toFixed(1)).join(' × ')} mm · ${fragment.volumeMm3.toFixed(2)} mm³`;
      button.onclick = () => focusFragment(index);
      const item = document.createElement('div'),
        reason = document.createElement('p');
      reason.className = 'field-note';
      reason.textContent =
        fragment.fillUnavailableReason ||
        'Interior core: filling adds this volume to the candle, removing the loose mold material.';
      item.append(button, reason);
      return item;
    })
  );
  $('retry-grooves').hidden = !diagnosis.featureCauses.includes('band grooves');
  $('retry-inlets').hidden = !diagnosis.featureCauses.includes('pour inlets');
  $('retry-inlets').disabled = data.report.settings.pour <= 4;
  document
    .querySelectorAll('[data-view]')
    .forEach((b) => (b.disabled = b.dataset.view === 'walls'));
  if (diagnosisURL) URL.revokeObjectURL(diagnosisURL);
  diagnosisURL = URL.createObjectURL(
    new Blob([JSON.stringify(data.report, null, 2)], { type: 'application/json' })
  );
  $('download-diagnosis').href = diagnosisURL;
  draftMessage('Mold inspection available · detached regions are highlighted in red.', 'failed');
  message(
    data.report.fillAttempt
      ? 'Fill could not repair every region'
      : 'Detached regions found — inspect the red highlights',
    data.report.fillAttempt?.reasons?.length
      ? data.report.fillAttempt.reasons.join(' ')
      : `Affected: ${parts}. No printable kit was exported. Choose a region to zoom in, then try a suggested change.`,
    true
  );
  showAdjustment(
    data,
    data.report.adaptationAttempts?.length
      ? 'The tested changes did not pass for this split. Fill/smooth the highlighted recesses in the model, reduce inlet diameter, or increase the exterior wall. Your selected split is unchanged.'
      : 'Loose mold material prevents this split from being printable. Propose a local fill or directional model adjustment for these exact planes.'
  );
  if (!placing) setView('xray', true);
}

function splitLabel(report) {
  const s = report?.settings || settings();
  return `${s.pieces} parts · ${s.split === 'custom' ? `custom ${s.planeAngle}° / ${s.planeTilt}°` : s.split.toUpperCase()} plane`;
}

function showAdjustment(data, reason = '') {
  $('adjustment').hidden = false;
  $('adapt-model').hidden = false;
  $('adapt-model').disabled = !source || !form.elements.split.value;
  if ($('adjustment-actions')) $('adjustment-actions').hidden = false;
  $('adjustment-title').textContent = `Review release for ${splitLabel(data?.report)}`;
  $('adjustment-message').textContent =
    reason ||
    'Sampled pull paths have undercuts or cavity walls are thin. You can generate and download your mold kit with warnings, or propose model adjustments to eliminate undercuts.';
  const attempts = data?.report?.adaptationAttempts || [];
  $('proposal-summary').textContent = attempts.length
    ? attempts.map((attempt) => `${attempt.method}: ${attempt.reasons.join(' ')}`).join(' ')
    : 'A local core fill is tried first, followed by directional undercut fills. You can also generate the mold kit directly without model adjustments.';
  $('proposal-actions').hidden = true;
}

function showNeedsAdjustment(data) {
  draft = data;
  showWallSummary(data.report);
  document.querySelectorAll('[data-view]').forEach((b) => (b.disabled = false));
  const reasons = [];
  if (!data.report.pullCheck.passed)
    reasons.push(
      'Some sampled pull paths intersect the candle. Fill the recesses that hook behind these parts.'
    );
  showAdjustment(data, reasons.join(' '));
  draftMessage(
    'Preview ready · warnings detected (undercuts / thin walls). Click "Download with warnings" to prepare.'
  );
  message(
    'Release check needs review (warning)',
    `${reasons.join(' ')} You can download the mold kit with warnings, or propose model adjustments.`.trim()
  );
  if (!placing) setView('mold', true);
}

function showProposal(data) {
  pendingProposal = data;
  draft = data;
  $('adapt-model').hidden = true;
  $('adapt-model').disabled = true;
  if ($('adjustment-actions')) $('adjustment-actions').hidden = true;
  const p = data.report.proposal;
  $('adjustment').hidden = false;
  $('adjustment-title').textContent = `Proposed changes · ${splitLabel(data.report)}`;
  $('adjustment-message').textContent =
    `${p.method}. Added candle volume: ${p.addedCandleVolumeMm3.toFixed(3)} mm³. Amber shows the change. The selected plane and part count are unchanged.`;
  $('proposal-summary').textContent = [
    ...p.parameterChanges.map(
      (c) =>
        `${{ wall: 'Mold wall (mm)', band: 'Band channels', pour: 'Pour diameter (mm)', vent: 'Vent diameter (mm)', wick: 'Wick outlet (mm; 0 disables it)' }[c.setting] || c.setting}: ${c.from} → ${c.to}`
    ),
    `Candle dimensions: ${p.dimensionsMm.original.join(' × ')} → ${p.dimensionsMm.adjusted.join(' × ')} mm.`,
    p.limitations,
  ].join(' ');
  $('proposal-actions').hidden = false;
  showWallSummary(data.report);
  document.querySelectorAll('[data-view]').forEach((b) => (b.disabled = false));
  draftMessage(
    'Proposed model changes passed the implemented checks. Review amber changes and accept to download.'
  );
  message(
    'Review the adjusted model',
    'Accept to prepare the download, or keep your original model.'
  );
  setView('xray', true);
}

$('accept-proposal').onclick = () => {
  if (pendingProposal) showResults(pendingProposal);
};
$('reject-proposal').onclick = () => {
  adaptationEnabled = false;
  repairEnabled = false;
  invalidate();
};
if ($('generate-anyway')) {
  $('generate-anyway').onclick = () => {
    removeDetachedEnabled = true;
    form.requestSubmit();
  };
}
if ($('remove-detached')) {
  $('remove-detached').onclick = () => {
    removeDetachedEnabled = true;
    if (form.elements.removeDetached) form.elements.removeDetached.checked = true;
    message(
      'Removing detached walls',
      'Discarding disconnected mold material and generating clean printable parts…'
    );
    form.requestSubmit();
  };
}

function drawFilledRegions(data, lift) {
  if (!renderAvailable) return;
  if (!renderAvailable) return;
  for (const region of data?.filledRegions || []) {
    const mesh = addMesh(region.positions, 0xe3a02d, 0.9);
    mesh.position.z = lift;
    mesh.renderOrder = 20;
  }
}

function showFillReview(data) {
  const repair = data.report.repair;
  const adaptation = data.report.adaptation;
  $('fill-review').hidden = !repair?.applied && !adaptation?.applied;
  if (adaptation?.applied) {
    $('fill-review-summary').textContent =
      `Model adjusted for ${splitLabel(data.report)} · ${((adaptation.addedCandleVolumeMm3 || 0) + (repair?.addedCandleVolumeMm3 || 0)).toFixed(3)} mm³ added. Original model.stl is preserved; candle-adapted.stl contains the new shape.`;
    return;
  }
  if (repair?.applied)
    $('fill-review-summary').textContent =
      `${repair.regionCount} recess(es) filled · ${repair.addedCandleVolumeMm3.toFixed(4)} mm³ of added candle volume. ${data.type === 'diagnostic' ? 'Other detached regions still need attention.' : ''}`;
}

$('fill-detached').onclick = () => {
  repairEnabled = true;
  invalidate({ keepRepair: true });
  message(
    'Filling candle recesses',
    'The changed candle volume will be shown in amber. Review the result before downloading.'
  );
};
$('undo-fill').onclick = () => {
  repairEnabled = false;
  adaptationEnabled = false;
  invalidate();
};
$('adapt-model').onclick = () => {
  if (!source || !form.reportValidity()) return;
  invalidate();
  schedulePreview({ adapt: true });
};

function focusFragment(index) {
  if (!renderAvailable || draft?.type !== 'diagnostic') return;
  setView('xray');
  const fragment = draft.fragments[index],
    report = draft.report;
  const distance = (Number($('explode').value) / 100) * report.moldDimensions[0] * 0.8;
  const lift =
    report.displayLift + Math.max(0, ...report.pullDirections.map((n) => -n[2] * distance));
  const center = new THREE.Vector3()
    .fromArray(fragment.bounds.min)
    .add(new THREE.Vector3().fromArray(fragment.bounds.max))
    .multiplyScalar(0.5);
  center.addScaledVector(
    new THREE.Vector3().fromArray(report.pullDirections[fragment.part - 1]),
    distance
  );
  center.z += lift;
  const direction = camera.position.clone().sub(orbit.target).normalize();
  orbit.target.copy(center);
  camera.position
    .copy(center)
    .addScaledVector(direction, Math.max(30, ...fragment.dimensionsMm) * 3);
  orbit.update();
}

$('retry-grooves').onclick = () => {
  form.elements.band.checked = false;
  form.elements.band.dispatchEvent(new Event('input', { bubbles: true }));
};
$('retry-inlets').onclick = () => {
  form.elements.pour.value = Math.max(4, Math.floor(Number(form.elements.pour.value) * 0.7));
  form.elements.pour.dispatchEvent(new Event('input', { bubbles: true }));
};

form.addEventListener('submit', (event) => {
  event.preventDefault();
  if (!source || worker || !form.reportValidity()) return;
  const options = settings();
  buildingSettings = options;
  invalidate({ keepRepair: true });
  busy(true);
  $('progress').value = 0;
  message('Opening your workshop', 'Preparing the local geometry engine…');
  try {
    worker = new Worker(new URL('./worker.js', import.meta.url), { type: 'module' });
    worker.onmessage = ({ data }) => {
      if (data.type === 'progress') {
        $('progress').value = data.percent;
        message(data.message, 'Working locally. Detailed models can take a few minutes.');
      }
      if (data.type === 'error') {
        generationFailed('This model needs another look', data.message);
      }
      if (data.type === 'complete') {
        endWorker();
        showResults(data);
      }
      if (data.type === 'needs-adjustment') {
        endWorker();
        showNeedsAdjustment(data);
      }
      if (data.type === 'diagnostic') {
        endWorker();
        showDiagnosis(data);
      }
    };
    worker.onerror = () => {
      generationFailed(
        'The geometry worker stopped',
        'Your browser may have run out of memory. Simplify the model or set cavity clearance to 0.'
      );
    };
    timeout = setTimeout(() => {
      generationFailed(
        'This shape needs more time',
        'The 3-minute limit was reached. Simplify the model or set cavity clearance to 0.'
      );
    }, 180000);
    const copy = source.slice(0);
    worker.postMessage({ buffer: copy, settings: options, selected: true, force: true }, [copy]);
  } catch (error) {
    generationFailed('Could not start the geometry worker', error.message);
  }
});

$('cancel').onclick = () => {
  endWorker();
  message('Generation cancelled', 'Your model is still here. Adjust your settings and try again.');
  draftMessage('Generation cancelled. Edit a setting to rebuild the preview.');
};
$('file').onchange = (event) => loadFile(event.target.files[0]);
$('example').onclick = example;
form.addEventListener('input', (event) => {
  if (event.target.id === 'file' || worker) return;
  if (event.target.id === 'show-planes') return;
  if (event.target.id === 'radial-angle') {
    form.elements.split.value = 'custom';
    form.elements.planeAngle.value = event.target.value;
    form.elements.planeTilt.value = 0;
  }
  if (['rotateX', 'rotateY', 'rotateZ'].includes(event.target.name)) manualHoles = [];
  if (event.target.name === 'pourCount')
    manualHoles = manualHoles.slice(0, Number(event.target.value));
  if (event.target.name === 'pieces' && Number(event.target.value) > 2)
    form.elements.offset.value = 0;
  if (event.target.name === 'pieces' && Number(event.target.value) > 2) {
    form.elements.planeTilt.value = 0;
    if (form.elements.split.value === 'z') form.elements.split.value = 'x';
  }
  if (event.target.name === 'removeDetached') removeDetachedEnabled = event.target.checked;
  updatePlaneControls();
  invalidate({ keepRemoveDetached: true });
  message('Settings updated', 'Your preview and printable kit will rebuild automatically.');
});
function updatePlaneControls() {
  const radial = Number(form.elements.pieces.value) > 2;
  $('radial-controls').hidden = !radial;
  $('radial-angle').disabled = !radial;
  $('radial-angle').value =
    form.elements.split.value === 'custom'
      ? form.elements.planeAngle.value
      : form.elements.split.value === 'y'
        ? 90
        : 0;
  form.elements.offset.disabled = radial;
  form.elements.planeTilt.disabled = radial;
  form.elements.split.querySelector('option[value="z"]').disabled = radial;
  $('plane-controls').hidden = radial || form.elements.split.value !== 'custom';
}
function rotateSections(delta) {
  const input = $('radial-angle');
  const angle = delta === null ? 0 : Number(input.value || 0) + delta;
  input.value = ((angle + 180 + 360) % 360) - 180;
  input.dispatchEvent(new Event('input', { bubbles: true }));
}
$('radial-left').onclick = () => rotateSections(-15);
$('radial-right').onclick = () => rotateSections(15);
$('radial-reset').onclick = () => rotateSections(null);
$('show-walls').onclick = () => setView('walls', true);
$('show-planes').onchange = () => {
  if (currentView === 'model') modelPreview();
};
$('place-holes').onclick = () => {
  placement(!placing);
  modelPreview();
};
$('auto-holes').onclick = () => {
  manualHoles = [];
  invalidate();
};
host.addEventListener('pointerdown', (e) => {
  pointerStart = [e.clientX, e.clientY];
});
host.addEventListener('pointerup', (e) => {
  if (
    !placing ||
    !renderAvailable ||
    !shapeMesh ||
    !pointerStart ||
    Math.hypot(e.clientX - pointerStart[0], e.clientY - pointerStart[1]) > 5
  )
    return;
  const rect = host.getBoundingClientRect();
  const pointer = new THREE.Vector2(
    ((e.clientX - rect.left) / rect.width) * 2 - 1,
    (-(e.clientY - rect.top) / rect.height) * 2 + 1
  );
  const ray = new THREE.Raycaster();
  ray.setFromCamera(pointer, camera);
  const hit = ray.intersectObject(shapeMesh)[0];
  if (!hit) return;
  if (hit.face.normal.z < 0.15) {
    $('hole-note').textContent =
      'Choose an upward-facing surface so the vertical inlet reaches the cavity.';
    $('hole-note').classList.add('hole-feedback');
    return;
  }
  const point = hit.point
    .toArray()
    .map((n, i) =>
      Math.max(0, Math.min(1, (n - shapeBounds.min[i]) / (shapeBounds.max[i] - shapeBounds.min[i])))
    );
  const count = Number(form.elements.pourCount.value);
  if (manualHoles.length >= count) manualHoles = [];
  manualHoles.push(point);
  invalidate();
});
const dropzone = $('dropzone');
dropzone.addEventListener('dragover', (e) => {
  e.preventDefault();
  dropzone.classList.add('drag');
});
dropzone.addEventListener('dragleave', () => dropzone.classList.remove('drag'));
dropzone.addEventListener('drop', (e) => {
  e.preventDefault();
  dropzone.classList.remove('drag');
  loadFile(e.dataTransfer.files[0]);
});
document
  .querySelectorAll('[data-view]')
  .forEach((b) => (b.onclick = () => setView(b.dataset.view, true)));
$('explode').oninput = () => setView(currentView);
$('reset-camera').onclick = resetCamera;
initializeViewer();
if (renderAvailable) {
  camera.position.set(180, -240, 180);
  orbit.target.set(0, 0, 40);
  orbit.update();
}
